import { Box, Button, DialogActions, Divider, Grid, IconButton, InputAdornment, Stack, Tooltip } from '@mui/material';
import { Formik, Field } from 'formik';
import { TextField } from 'formik-mui';
import { initialValues, validate, FormValues } from '../form';
import { Dialog } from 'shared/ui/dialog';
import { createNewUser } from '../model';
import { useNavigate } from 'react-router-dom';
import { useSnack } from 'shared/ui/snack';
import { userLinkManager } from 'utils/link-manager';
import { UserRolePicker } from 'features/user/user-role-picker/user-role-picker';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';
import { generateLogin, generatePassword } from 'utils/generate-auth-helper';
import { userListApi } from 'entities/user/users-list/api';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const AddUserDialog = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnack();
  const [showPassword, setShowPassword] = useState(false);
  const { data: existingLogins } = userListApi.useGetAllLoginQuery();

  const handleSubmit = async (values: FormValues) => {
    createNewUser(values)
      .then(response => {
        showSuccess('Пользователь успешно добавлен');
        navigate(userLinkManager.link(response.id));
      })
      .catch(error => {
        showError('Ошибка добавления пользователя: ' + error.message);
      });
  };

  const handleGeneratePassword = (setFieldValue: (field: string, value: string) => void) => {
    const newPassword = generatePassword();
    setFieldValue('password1', newPassword);
    setFieldValue('password2', newPassword);
  };

  const handleGenerateLogin = (setFieldValue: (field: string, value: string) => void, values: FormValues) => {
    if (!values.secondName || !values.firstName || !values.middleName) {
      showError('Для генерации логина необходимо заполнить поля фамилия, имя и отчество');
      return;
    }

    try {
      const login = generateLogin(
        values.secondName, 
        values.firstName, 
        values.middleName,
        existingLogins || []
      );
      setFieldValue('login', login);
    } catch (error) {
      showError('Ошибка при генерации логина');
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
    >
      {({ submitForm, isSubmitting, setFieldValue, values }) => (
        <Dialog onClose={onClose} open={open} maxWidth='xl'>
          <Dialog.Title>Новый пользователь</Dialog.Title>
          <Dialog.Content>
            <Box sx={{ minWidth: 650 }}>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                <Field
                  name='secondName'
                  label='Фамилия'
                  required
                  fullWidth
                  component={TextField}
                />
                <Field
                  name='firstName'
                  label='Имя'
                  required
                  fullWidth
                  component={TextField}
                />
                <Field
                  name="middleName"
                  label='Отчество'
                  required
                  fullWidth
                  component={TextField}
                />
                <Field
                  name='roles'
                  label='Роли пользователя'
                  required
                  fullWidth
                  component={UserRolePicker}
                />
                <Divider />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 !important',
                    minWidth: 'auto'
                  }}>
                    <Field
                      name='login'
                      label='Логин'
                      required
                      fullWidth
                      component={TextField}
                    />
                  </Grid>
                  <Grid item xs={2}  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 !important',
                    minWidth: 'auto'
                  }}>
                    <Tooltip title="Сгенерировать логин">
                      <IconButton color="primary" aria-label="generate-login" size="large"
                        onClick={() => handleGenerateLogin(setFieldValue, values)}>
                        <AutoFixHighIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10} sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 !important',
                    minWidth: 'auto'
                  }}>
                    <Field
                      name='password1'
                      label='Пароль'
                      required
                      fullWidth
                      component={TextField}
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 !important',
                    minWidth: 'auto'
                  }}>
                    <Tooltip title="Сгенерировать пароль">
                      <IconButton color="primary" aria-label="generate-password" size="large"
                        onClick={() => handleGeneratePassword(setFieldValue)}>
                        <AutoFixHighIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={10} sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 !important',
                    minWidth: 'auto'
                  }}>
                    <Field
                      name='password2'
                      label='Пароль'
                      required
                      fullWidth
                      component={TextField}
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </Dialog.Content>
          <DialogActions>
            <Button variant="contained"
              autoFocus
              onClick={submitForm}
              disabled={isSubmitting}>
              {isSubmitting ? 'Подождите...' : 'Сохранить'}
            </Button>
            <Button variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}>
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>)
      }
    </Formik>
  );
};


